import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';

function CreditNotesIndex() {
    const [creditNotes, setCreditNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        loadCreditNotes();
    }, [search, currentPage, statusFilter]);

    const loadCreditNotes = async () => {
        try {
            setLoading(true);
            const params: any = { q: search, page: currentPage };
            if (statusFilter) params.status = statusFilter;

            const response = await api.getCreditNotes(params);
            if (response.success) {
                setCreditNotes(response.data || []);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            } else {
                setError(response.message || 'Failed to load credit notes');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'issued': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
            case 'applied': return 'bg-blue-50 text-blue-700 border-blue-100';
            case 'void': return 'bg-slate-50 text-slate-700 border-slate-100';
            default: return 'bg-slate-50 text-slate-700 border-slate-100';
        }
    };

    if (loading && creditNotes.length === 0) {
        return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Loading credit notes...</div>;
    }

    return (
        <div className="mt-16 pb-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Credit Notes</h1>
                    <p className="text-slate-500 mt-1">Manage customer credit notes from returns ({pagination.total_count} found)</p>
                </div>
            </div>

            <div className="mb-6 space-y-4">
                <div className="relative">
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search credit notes by number, return or customer..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all shadow-sm shadow-slate-100"
                    />
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => { setStatusFilter(''); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${!statusFilter ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-600/20' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'}`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => { setStatusFilter('issued'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${statusFilter === 'issued' ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-600/20' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:text-emerald-600'}`}
                    >
                        Issued
                    </button>
                    <button
                        onClick={() => { setStatusFilter('applied'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${statusFilter === 'applied' ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/20' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'}`}
                    >
                        Applied
                    </button>
                    <button
                        onClick={() => { setStatusFilter('void'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${statusFilter === 'void' ? 'bg-slate-600 text-white border-slate-600 shadow-lg shadow-slate-600/20' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-600'}`}
                    >
                        Void
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6 shadow-sm">
                    <p className="text-sm text-rose-700 font-medium">{error}</p>
                </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                {creditNotes.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                <tr>
                                    <th className="px-6 py-4 text-left">Credit Note #</th>
                                    <th className="px-6 py-4 text-left">Return #</th>
                                    <th className="px-6 py-4 text-left">Customer</th>
                                    <th className="px-6 py-4 text-left">Issue Date</th>
                                    <th className="px-6 py-4 text-center">Status</th>
                                    <th className="px-6 py-4 text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {creditNotes.map((creditNote: any) => (
                                    <tr key={creditNote.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="font-mono font-bold text-indigo-600">
                                                {creditNote.credit_note_number}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Link to={`/returns/${creditNote.return_order_id}`} className="text-slate-600 hover:text-indigo-600 font-medium">
                                                {creditNote.return_number}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 text-slate-900 font-medium">{creditNote.customer_name}</td>
                                        <td className="px-6 py-4 text-slate-500">
                                            {new Date(creditNote.issue_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider ${getStatusColor(creditNote.status)}`}>
                                                {creditNote.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono font-bold text-emerald-600 uppercase tracking-tighter">
                                            ₹{creditNote.amount?.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="py-20 text-center">
                        <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No credit notes found</h3>
                        <p className="text-slate-500 max-w-sm mx-auto">Try adjusting your filters or search terms.</p>
                    </div>
                )}
            </div>

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                perPage={pagination.per_page}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default CreditNotesIndex;
